import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import { setMessage } from '../../store/reducers/appSlice';
import { updateShopName } from '../../lib/api/shop/update_shop';
import { submitLogToBugsnag } from '../../lib/api/log';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import MainSquareButton from '../../ui/buttons/MainSquareButton';

interface RenameBrandFormProps {
  shopSlug: string;
  currentName: string;
  onClose: () => void;
}

const MAX_NAME_LENGTH = 255;

const RenameBrandForm: React.FC<RenameBrandFormProps> = ({
  shopSlug,
  currentName,
  onClose,
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState(currentName || '');
  const [loading, setLoading] = useState(false);

  const allShops = useSelector(
    (state: RootState) => state.user.profile.all_shops
  );

  const trimmed = name.trim();
  const isValid =
    isNotEmptyString(trimmed) &&
    trimmed.length <= MAX_NAME_LENGTH &&
    trimmed !== currentName.trim();

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;

    setLoading(true);
    try {
      const res = await updateShopName(shopSlug, trimmed);

      if (!res.success) {
        throw new Error(res.message || 'Failed to update brand name');
      }

      // Patch the renamed shop in place — no full re-init needed.
      const updatedShops = (allShops ?? []).map((shop) =>
        shop.slug === shopSlug ? { ...shop, name: trimmed } : shop
      );
      dispatch(updateProfile({ all_shops: updatedShops }));

      dispatch(
        setMessage({
          message: 'Brand name updated.',
          messageType: 'success',
        })
      );
      onClose();
    } catch (error: any) {
      console.error('Error renaming brand:', error);
      submitLogToBugsnag('error', `Error renaming brand: ${error}`);
      dispatch(
        setMessage({
          message: error.message || 'Failed to update brand name',
          messageType: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submitForm} className='renameBrandForm'>
      <h3 className='renameBrandForm__title'>Rename brand</h3>
      <div className='renameBrandForm__input'>
        <input
          type='text'
          value={name}
          autoFocus
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => setName(e.target.value)}
          placeholder='Enter brand name'
          className='renameBrandForm__field'
          disabled={loading}
        />
      </div>
      <div className='renameBrandForm__actions'>
        <button
          type='button'
          className='renameBrandForm__cancel'
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <MainSquareButton
          type='submit'
          value='Save'
          normal={true}
          loading={loading}
          disabled={!isValid}
        />
      </div>
    </form>
  );
};

export default RenameBrandForm;
